/**
 * AgentRow — Card moderno de agente (estilo Vercel/Linear)
 * Desktop: row compacto com colunas
 * Mobile: card empilhado
 */
import { useState, useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { api } from '../../api/client';
import { showToast } from '../../components/Toast';
import { T } from '../../styles/tokens';
import { Avatar, Badge, ChannelBadge, Icon, Button, IconButton } from '../../components/ui';
import { IntegrationIcon } from '../../components/IntegrationIcon';
import type { Environment, AgentInfo } from './types';

interface AgentRowProps {
  agent: AgentInfo;
  env: Environment;
  userPlan?: string;
  isMain?: boolean;
}

export function AgentRow({ agent, env, userPlan, isMain }: AgentRowProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [avatarHover, setAvatarHover] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>((agent as any).avatar_url || null);
  const [uploading, setUploading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarUpload = async (e: Event) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { showToast('Máximo 2MB', 'error'); return; }
    setUploading(true);
    try {
      const wsId = (env.workspace_id || env.id || '').replace(/^ws-/, '');
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await fetch(`/api/v1/cloud/${wsId}/avatar`, {
        method: 'PUT',
        body: formData,
        credentials: 'include',
      });
      const data = await res.json() as any;
      if (data.data?.avatar_url) {
        setAvatarUrl(data.data.avatar_url + '?t=' + Date.now());
        showToast('Avatar atualizado!', 'success');
      } else {
        showToast(data.error?.message || 'Erro ao enviar', 'error');
      }
    } catch (err: any) {
      showToast(err.message || 'Erro ao enviar avatar', 'error');
    } finally {
      setUploading(false);
      input.value = '';
    }
  };

  const name = agent.agent_name || agent.name || 'Agente';
  const agentModel = agent.agent_model || agent.model || '';
  const brainSlug = agent.agent_brain || agent.brain || '';
  const brainName = agent.brain_name || brainSlug;
  const channels: string[] = agent.channels || [];
  // Filtrar canais conhecidos das integrações (evitar duplicata)
  const KNOWN_CHANNELS = ['telegram', 'whatsapp', 'discord', 'slack', 'signal', 'imessage', 'irc', 'googlechat'];
  const integrations: string[] = (agent.integrations || []).filter((i: string) => !KNOWN_CHANNELS.includes(i.toLowerCase()));
  const skills: string[] = agent.skills || [];
  const isOnline = env.online;
  const envDisplayName = (env.name || '').replace(/\.(hstgr\.cloud|elon\.cloud)$/i, '');

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const wsId = (env.workspace_id || env.id || '').replace(/^ws-/, '');
  const chatUrl = `/chat/agent/${encodeURIComponent(wsId)}`;

  const funcionLabel = brainSlug && brainSlug !== 'clean' ? brainName : (skills.length > 0 ? `${skills.length} skill${skills.length !== 1 ? 's' : ''}` : '');

  const usageLabel = env.usage && (env.usage.today_cost > 0 || env.usage.month_cost > 0)
    ? `$${env.usage.today_cost.toFixed(2)} hoje · $${env.usage.month_cost.toFixed(2)} mês`
    : '';

  // Modelo formatado curto
  const modelShort = agentModel
    ? agentModel
        .replace('anthropic/', '')
        .replace('openai/', '')
        .replace('google/', '')
        .replace(/-\d{8}$/, '')  // remove date suffixes
    : '';

  // Menu dropdown
  const DropdownMenu = () => menuOpen ? (
    <div style={{
      position: 'absolute', right: 0, top: 32, zIndex: 100,
      background: '#1e1e1e', border: `1px solid ${T.border}`, borderRadius: 10,
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      minWidth: 180, padding: '4px 0',
    }}>
      <Button variant="ghost" disabled
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', fontSize: 12, background: 'none', border: 'none', opacity: 0.4, cursor: 'not-allowed' }}
      >
        <Icon name="settings" size={13} /> Configurar
      </Button>
    </div>
  ) : null;

  // Avatar com hover edit
  const AvatarWithEdit = ({ size }: { size: 'sm' | 'md' | 'lg' }) => (
    <div
      style={{
        position: 'relative', cursor: 'pointer', flexShrink: 0,
      }}
      onMouseEnter={() => setAvatarHover(true)}
      onMouseLeave={() => setAvatarHover(false)}
      onClick={() => fileInputRef.current?.click()}
    >
      <Avatar name={name} src={avatarUrl || undefined} size={size} status={isOnline ? 'online' : 'offline'} />
      {(avatarHover || uploading) && (
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'rgba(0,0,0,0.6)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          {uploading
            ? <div style={{ width: 12, height: 12, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
            : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
          }
        </div>
      )}
      <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{ display: 'none' }} onChange={handleAvatarUpload} />
    </div>
  );

  // Status pill
  const StatusDot = () => (
    <span style={{
      display: 'inline-block',
      width: 7, height: 7, borderRadius: '50%',
      background: isOnline ? '#22c55e' : '#6b7280',
      boxShadow: isOnline ? '0 0 6px rgba(34,197,94,0.5)' : 'none',
      flexShrink: 0,
    }} />
  );

  return (
    <>
      {/* ═══ DESKTOP (>768px) ═══ */}
      <div
        class="agent-row-desktop"
        onClick={() => route(chatUrl)}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 16px',
          borderBottom: `1px solid ${T.border}`,
          transition: 'background 0.15s',
          cursor: 'pointer',
        }}
        onMouseEnter={(e: any) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
        onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}
      >
        {/* Avatar */}
        <div onClick={(e: any) => e.stopPropagation()}>
          <AvatarWithEdit size="md" />
        </div>

        {/* Nome + Máquina + Modelo */}
        <div style={{ flex: 2.5, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {name}
            </span>
          </div>
          <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
            {envDisplayName && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Icon name="cloud" size={10} />{envDisplayName}
              </span>
            )}
            {modelShort && (
              <>
                <span style={{ color: T.textMuted || T.textSecondary, opacity: 0.4 }}>·</span>
                <span style={{ opacity: 0.8 }}>{modelShort}</span>
              </>
            )}
          </div>
        </div>

        {/* Canais */}
        <div style={{ flex: 0.8, minWidth: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
          {channels.length > 0 ? channels.map((ch: string) => (
            <ChannelBadge key={ch} name={ch} iconOnly iconSize={18} />
          )) : <span style={{ fontSize: 11, color: T.textMuted || T.textSecondary }}>—</span>}
        </div>

        {/* Integrações */}
        <div style={{ flex: 0.8, minWidth: 0 }}>
          {integrations.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {integrations.slice(0, 5).map((intName, i) => (
                <div
                  key={intName}
                  style={{
                    width: 26, height: 26, borderRadius: '50%', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    background: T.bgCard, border: `2px solid ${T.bg}`,
                    marginLeft: i > 0 ? -8 : 0, zIndex: 5 - i,
                    position: 'relative',
                  }}
                  title={intName}
                >
                  <IntegrationIcon name={intName} size={14} />
                </div>
              ))}
              {integrations.length > 5 && (
                <div style={{
                  width: 26, height: 26, borderRadius: '50%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  background: T.bgHover, border: `2px solid ${T.bg}`,
                  marginLeft: -8, fontSize: 9, fontWeight: 700, color: T.textSecondary,
                }}>+{integrations.length - 5}</div>
              )}
            </div>
          ) : <span style={{ fontSize: 11, color: T.textMuted || T.textSecondary }}>—</span>}
        </div>

        {/* Uso */}
        <div style={{ flex: 1.2, minWidth: 0, textAlign: 'right' as const }}>
          {usageLabel ? (
            <span style={{ fontSize: 11, fontFamily: 'monospace', color: T.textSecondary }}>{usageLabel}</span>
          ) : <span style={{ fontSize: 11, color: T.textMuted || T.textSecondary }}>—</span>}
        </div>

        {/* Ações */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }} onClick={(e: any) => e.stopPropagation()}>
          <Button variant="ghost" size="xs" onClick={() => route(chatUrl)}
            icon={<Icon name="chat" size={13} />}
            style={{ borderRadius: 8, padding: '6px 12px', fontSize: 11.5, fontWeight: 500 }}
          >Chat</Button>
          <div ref={menuRef} style={{ position: 'relative' }}>
            <IconButton icon={<Icon name="more" size={14} />} onClick={() => setMenuOpen(!menuOpen)} size={28} active={menuOpen} title="Mais opções" style={{ borderRadius: 8 }} />
            <DropdownMenu />
          </div>
        </div>
      </div>

      {/* ═══ MOBILE (<=768px) ═══ */}
      <div
        class="agent-row-mobile"
        onClick={() => route(chatUrl)}
        style={{
          padding: '16px',
          borderBottom: `1px solid ${T.border}`,
          cursor: 'pointer',
          transition: 'background 0.15s',
        }}
      >
        {/* Top row: Avatar + Info + Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div onClick={(e: any) => e.stopPropagation()}>
            <AvatarWithEdit size="md" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {name}
              </span>
            </div>
            <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2, display: 'flex', alignItems: 'center', gap: 5 }}>
              {envDisplayName && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Icon name="cloud" size={10} />{envDisplayName}
                </span>
              )}
              {modelShort && (
                <>
                  <span style={{ opacity: 0.3 }}>·</span>
                  <span style={{ opacity: 0.7 }}>{modelShort}</span>
                </>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }} onClick={(e: any) => e.stopPropagation()}>
            <IconButton
              icon={<Icon name="chat" size={15} />}
              onClick={() => route(chatUrl)}
              size={32}
              title="Chat"
              style={{ borderRadius: 8, background: 'rgba(255,255,255,0.06)' }}
            />
            <div ref={!menuOpen ? undefined : menuRef} style={{ position: 'relative' }}>
              <IconButton icon={<Icon name="more" size={14} />} onClick={() => setMenuOpen(!menuOpen)} size={32} title="Mais opções" style={{ borderRadius: 8 }} />
              <DropdownMenu />
            </div>
          </div>
        </div>

        {/* Bottom row: Chips (canais, integrações, uso) */}
        {(channels.length > 0 || integrations.length > 0 || usageLabel) && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, marginTop: 10,
            paddingLeft: 42, // alinhado com o nome (avatar 32 + gap 10)
            flexWrap: 'wrap',
          }}>
            {channels.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {channels.map((ch: string) => <ChannelBadge key={ch} name={ch} iconOnly iconSize={16} />)}
              </div>
            )}
            {integrations.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {integrations.slice(0, 4).map((intName, i) => (
                  <div key={intName} style={{
                    width: 22, height: 22, borderRadius: '50%', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    background: T.bgCard, border: `2px solid ${T.bg}`,
                    marginLeft: i > 0 ? -6 : 0, zIndex: 4 - i, position: 'relative',
                  }} title={intName}>
                    <IntegrationIcon name={intName} size={12} />
                  </div>
                ))}
                {integrations.length > 4 && (
                  <span style={{ fontSize: 10, color: T.textSecondary, marginLeft: 4 }}>+{integrations.length - 4}</span>
                )}
              </div>
            )}
            {usageLabel && (
              <span style={{ fontSize: 10.5, fontFamily: 'monospace', color: T.textSecondary, marginLeft: 'auto' }}>{usageLabel}</span>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export const AgentCard = AgentRow;
